import { useCallback, useContext, useState } from "react";
import { Heart } from "lucide-react";
import { container } from "tsyringe";
import { match } from "ts-pattern";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import Button, { EButtonColor, EButtonVariant } from "components/elements/Button";
import { UserContext } from "contexts/User";
import ProjectService from "services/ProjectService";
import classes from "./classes.module.scss";

type IProps = {
	project: Pick<ProjectResponseResource, "id" | "likes">;
};

const projectService = container.resolve(ProjectService);

export default function ProjectLikeButton(props: IProps) {
	const { user } = useContext(UserContext);
	const [toggledTo, setToggledTo] = useState<{ liked: boolean } | null>(null);

	const isPreviouslyLiked = useCallback(() => {
		return props.project.likes.map((like) => like.userId).includes(user!.id);
	}, [props.project.likes, user]);

	const toggleLike = useCallback(() => {
		projectService.likeProject(props.project.id).then((toggledTo) => setToggledTo(toggledTo));
	}, [props.project.id]);

	const isLiked = useCallback(() => {
		return match(toggledTo)
			.with({ liked: true }, () => true)
			.with({ liked: false }, () => false)
			.otherwise(() => isPreviouslyLiked());
	}, [isPreviouslyLiked, toggledTo]);

	const getLikesCount = useCallback(() => {
		return match(toggledTo)
			.with({ liked: true }, () => props.project.likes.length + (isPreviouslyLiked() ? 0 : 1))
			.with({ liked: false }, () => props.project.likes.length - (isPreviouslyLiked() ? 1 : 0))
			.otherwise(() => props.project.likes.length);
	}, [isPreviouslyLiked, props.project.likes.length, toggledTo]);

	return isLiked() ? (
		<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} className={classes["root"]} onClick={toggleLike}>
			<Heart fill={"black"} className={classes["icon"]} />
			{getLikesCount()}
		</Button>
	) : (
		<Button variant={EButtonVariant.OUTLINED} color={EButtonColor.PRIMARY} className={classes["root"]} onClick={toggleLike}>
			<Heart className={classes["icon"]} />
			{getLikesCount()}
		</Button>
	);
}
